import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Select,
  Col,
  Form,
  Modal,
  Row,
  Typography,
  Button,
  Tooltip,
  Space,
  Alert,
} from 'antd';
import { ColumnsType } from 'antd/es/table/interface';

import {
  FilterTypeEnum,
  ListStockItemsQuery,
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';

import { useStockItems } from '@/core/domain/stock/StockItems/StockItems.hooks';

import { ModalProps } from '@/types/ModalProps';
import { FieldData } from '@/types/FieldData';

import AppPageHeader from '@/components/_common/AppPageHeader';
import ResultOrderFilterFormItem from '@/components/_common/ResultOrderFilterFormItem';
import { useDebounce } from '@/hooks';
import { Info } from 'react-feather';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { InfiniteTable } from '@/components/_common';
import usePartsColumns from '@/components/Operational/Inventory/Parts/usePartsColumns';
import partsOrderColumns from '@/utils/functions/partsOrderColumns/partsOrderColumns';
import { getUserFromLocalStorage } from '@/utils/user';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';

const { Title, Text } = Typography;

interface StockItemsModalProps extends ModalProps {
  companyId?: number;
  selectedItems?: StockItem[];
  finishCallback?: (stockItems?: StockItem[]) => void;
  itemType?: StockItemType;
  onSelectPart?: (part: StockItem) => void;
  partsSelected?: number[];
  directSale?: boolean;
  noSelection?: boolean;
}

const StockItemsModal: React.FC<StockItemsModalProps> = ({
  visible,
  handleVisible,
  companyId,
  selectedItems = [],
  finishCallback,
  itemType,
  onSelectPart,
  partsSelected,
  directSale,
  noSelection,
}: StockItemsModalProps) => {
  const intl = useIntl();
  const { companyIds } = getUserFromLocalStorage();

  const [queryParams, setQueryParams] = useState<ListStockItemsQuery>({
    order: 'ASC',
    itemType: StockItemType.Part,
    active: true,
  });

  const [selectedStockItemsKeys, setSelectedStockItemsKeys] = useState<
    StockItem[]
  >([]);

  const partsColumns = usePartsColumns();

  const matrix = useMatrix(visible);

  const handleFilterByCompanyId = () => {
    if (matrix.data?.matrixSettings.canViewMultipleCompaniesStockInDirectSale) {
      return companyIds?.split(',').map(Number);
    }
    return companyId ? [companyId] : [];
  };

  useEffect(() => {
    if (visible) {
      setQueryParams((prev) => ({
        ...prev,
        companiesIds: handleFilterByCompanyId(),
      }));

      setSelectedStockItemsKeys(selectedItems);
    }
  }, [visible, companyId, selectedItems, matrix.dataUpdatedAt]);

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } = useStockItems(
    visible && !!queryDebounce?.companiesIds,
    queryDebounce,
  );
  const stockItems = flatInfiniteResult(data);

  const handleOk = (): void => {
    if (finishCallback) {
      finishCallback(selectedStockItemsKeys);
    }

    handleCancel();
  };

  const handleCancel = (): void => {
    setQueryParams(itemType ? { itemType, active: true } : { active: true });
    setSelectedStockItemsKeys([]);

    handleVisible();
  };

  const handleFiltersChange = (_: FieldData[], allFields: FieldData[]) => {
    allFields.forEach((field) => {
      const name = field.name as string[];
      const { value } = field;

      if (value) {
        setQueryParams((prev) => ({
          ...prev,
          [name[0]]: value,
        }));
      }
    });
  };

  const columns: ColumnsType<StockItem> = [
    ...partsColumns.columns,
    onSelectPart
      ? {
          dataIndex: 'actions',
          align: 'right',
          fixed: 'right',
          render: (_, row) => {
            if (!companyId) return '-';

            const companyStock = row.stock?.find(
              (stockItem) => companyId === stockItem.company.id,
            );

            return (
              <Button
                disabled={
                  !companyStock?.availableStock ||
                  (row.partId || row.consumableId
                    ? partsSelected?.includes(row.partId || row.consumableId)
                    : false)
                }
                type="primary"
                onClick={() => onSelectPart(row)}
              >
                <FormattedMessage id="diagnostics.modal.button.select" />
              </Button>
            );
          },
          width: 170,
        }
      : {},
  ];

  return (
    <Modal
      title={intl.formatMessage({ id: 'stockCorrections.domain.items.add' })}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width="90%"
      destroyOnClose
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>
                <Space>
                  <FormattedMessage id="stockCorrections.create.items.search" />

                  {!directSale && (
                    <Tooltip
                      title={
                        'Limite máximo de itens na correção: 50. Para adicionar mais itens, favor realizar um Inventário.'
                      }
                    >
                      <Info size={20} cursor="pointer" />
                    </Tooltip>
                  )}
                </Space>
              </Title>
              <Text>
                {intl.formatMessage({
                  id: directSale
                    ? 'directSale.create.items.searchMessage'
                    : 'stockCorrections.create.items.searchMessage',
                })}
              </Text>
            </div>

            <AppPageHeader
              onSearch={(value: string) =>
                setQueryParams((prev) => ({ ...prev, search: value }))
              }
              extraFilters={
                <Form onFieldsChange={handleFiltersChange}>
                  <Row gutter={[16, 16]}>
                    <Col span={24} md={8}>
                      <Form.Item
                        noStyle
                        name="filterType"
                        initialValue={FilterTypeEnum.free}
                      >
                        <Select
                          style={{ width: '100%' }}
                          options={[
                            {
                              label: 'Por campo - Livre',
                              value: FilterTypeEnum.free,
                            },
                            {
                              label: 'Por campo - Especifico',
                              value: FilterTypeEnum.specific,
                            },
                          ]}
                          onChange={(value) =>
                            setQueryParams((prev) => ({
                              ...prev,
                              filterType: value,
                            }))
                          }
                        />
                      </Form.Item>
                    </Col>

                    {!itemType && (
                      <Col span={24} md={8}>
                        <Form.Item noStyle name="itemType">
                          <Select
                            allowClear
                            onClear={() =>
                              setQueryParams((prev) => ({
                                ...prev,
                                itemType: undefined,
                              }))
                            }
                            style={{ width: '100%' }}
                            placeholder={
                              <FormattedMessage id="stockCorrections.domain.items.type" />
                            }
                          >
                            <Select.Option value={1}>
                              <FormattedMessage id="stockCorrections.domain.items.type.item_type.part" />
                            </Select.Option>
                            <Select.Option value={2}>
                              <FormattedMessage id="stockCorrections.domain.items.type.item_type.consumable" />
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    )}

                    <Col span={24} md={8}>
                      <ResultOrderFilterFormItem />
                    </Col>
                  </Row>
                </Form>
              }
            />

            {selectedStockItemsKeys.length > 49 && (
              <Alert
                type="warning"
                description="Limite máximo de itens na correção: 50. Para adicionar mais itens, favor realizar um Inventário."
              />
            )}

            <InfiniteTable<StockItem>
              columns={columns}
              dataSource={stockItems}
              loading={isFetching || partsColumns.isFetching}
              rowKey={(row) => row.partId}
              pagination={false}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              onChange={(_p, _f, sorter) => {
                if (!Array.isArray(sorter)) {
                  partsOrderColumns(sorter, setQueryParams);
                }
              }}
              rowSelection={
                !noSelection
                  ? {
                      preserveSelectedRowKeys: true,
                      onChange: (keys, parts) => {
                        setSelectedStockItemsKeys([
                          ...parts.filter((part) => !!part),
                          ...selectedItems.filter(
                            (item) => !keys.includes(item.partId),
                          ),
                        ]);
                      },
                      selectedRowKeys: selectedStockItemsKeys.map(
                        (part) => part?.partId,
                      ),
                      getCheckboxProps: (item) => ({
                        disabled:
                          selectedStockItemsKeys.length > 49 ||
                          !!selectedItems.find(
                            (workorderItem) =>
                              workorderItem.partId === item.partId ||
                              workorderItem.consumableId === item.consumableId,
                          ),
                      }),
                    }
                  : undefined
              }
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default StockItemsModal;
